import React, { useState } from 'react';
import { ISchemaType } from '../../models/Schema.model';
import { Formik } from 'formik';
import { Button, Form, Modal } from 'react-bootstrap';
import TextField from '../shared/formikFields/TextField';
import { debounceEventHandler } from '../shared/submitFormUtility';
import { locale } from '../../common/localization/localizationService';
import yup from '../../common/validation';
import SurveyService from '../../services/surveyService';
import { Toastr } from '../../utils/Toastr';
import { Spinner2 } from '../shared/spinner2';

export interface ISurveyTypeDialogProps {
  customerId: number;
  show: boolean;
  surveyType: ISchemaType | null;
  onHide: (rerender: boolean) => void;
}

export default function SurveyTypeDialog(
  props: ISurveyTypeDialogProps
): JSX.Element {
  const isNew = !props.surveyType;

  const [isLoading, setLoading] = useState<boolean>(false);

  const onSubmit = async (
    surveyType: ISchemaType,
    isNew: boolean
  ): Promise<void> => {
    console.log(surveyType, isNew);
    if (isNew) {
      try {
        setLoading(true);
        await SurveyService.createSchemaType(surveyType);
        Toastr.success(locale.surveyTypesPage._createSuccess);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setLoading(false);
        props.onHide(true);
      }
    } else {
      try {
        setLoading(true);
        await SurveyService.editSchemaType(surveyType);
        Toastr.success(locale.surveyTypesPage._editSuccess);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setLoading(false);
        props.onHide(true);
      }
    }
  };

  const getSchema = () => {
    return yup.object({
      name: yup.string().required(locale.surveyTypesPage._typeNameRequired),
      type: yup
        .string()
        .required(locale.surveyTypesPage._typeKeyOptionRequired),
      key: yup.string().when('type', {
        is: 'container',
        then: (schema: any) =>
          schema.required(locale.surveyTypesPage._typeKeyRequired),
        otherwise: (schema: any) => schema.notRequired(),
      }),
    });
  };

  const newSurveyType = (): ISchemaType => ({
    customerId: props.customerId,
    key: '',
    name: '',
    surveyTypeId: undefined,
  });

  const surveyType = isNew ? newSurveyType() : props.surveyType;

  const getInitialType = (key: string | undefined) => {
    if (key === 'dayStart') return 'dayStart';
    if (key === 'dayEnd') return 'dayEnd';
    if (key) return 'container';
    return '';
  };

  return (
    <Formik
      onSubmit={(values: any) => {
        const finalKey = values.type === 'container' ? values.key : values.type;
        const result: ISchemaType = {
          ...surveyType,
          customerId: props.customerId,
          name: values.name,
          key: finalKey,
        };
        console.log('Saving SurveyType:', result);
        onSubmit(result, isNew);
      }}
      initialValues={{
        name: surveyType.name,
        key: surveyType.key,
        type: getInitialType(surveyType.key),
      }}
      validationSchema={getSchema()}
    >
      {({ handleSubmit, values, setFieldValue }) => (
        <Modal
          show={props.show}
          onHide={() => props.onHide(false)}
          className="survey-type-dialog"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isNew
                ? locale.surveyTypesPage._newSchemaType
                : locale.surveyTypesPage._editSchemaType}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Spinner2 show={isLoading} />
            <Form noValidate onSubmit={handleSubmit}>
              <label>{locale.surveyTypesPage._nameLabel}</label>
              <TextField
                label={locale.surveyTypesPage._nameLabel}
                name="name"
                placeholder={locale.surveyTypesPage._namePlaceHolder}
              />

              <label>{locale.surveyTypesPage._keyOptionLabel}</label>
              <div className="mb-3">
                <Form.Check
                  inline
                  type="radio"
                  label={locale.surveyTypesPage._dayStart}
                  id="dayStartRadio"
                  checked={values.type === 'dayStart'}
                  onChange={() => {
                    setFieldValue('type', 'dayStart');
                    setFieldValue('key', 'dayStart');
                  }}
                />
                <Form.Check
                  inline
                  type="radio"
                  label={locale.surveyTypesPage._dayEnd}
                  id="dayEndRadio"
                  checked={values.type === 'dayEnd'}
                  onChange={() => {
                    setFieldValue('type', 'dayEnd');
                    setFieldValue('key', 'dayEnd');
                  }}
                />
                <Form.Check
                  inline
                  type="radio"
                  label={locale.surveyTypesPage._containerRelated}
                  id="containerRadio"
                  checked={values.type === 'container'}
                  onChange={() => {
                    setFieldValue('type', 'container');
                    if (values.type !== 'container') {
                      setFieldValue('key', '');
                    }
                  }}
                />
              </div>

              {values.type === 'container' && (
                <TextField
                  label=""
                  name="key"
                  placeholder={locale.surveyTypesPage._keyOptionLabel}
                />
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => props.onHide(false)}>
              {locale.surveyTypesPage._cancel}
            </Button>
            <Button
              type="submit"
              variant="primary"
              onClick={debounceEventHandler(handleSubmit, 250)}
            >
              {locale.surveyTypesPage._submit}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  );
}
